import { AccountBadge } from "@/src/components/AccountBadge";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { relativeTime } from "@/src/libs/relativeTime";
import { Panel } from "@/src/pages/Dashboard/components/Panel";
import { FEED_PREVIEW } from "@/src/pages/Dashboard/hooks/useFeedPulse";
import { ScoreBadge } from "@/src/pages/Posts/components/ScoreBadge";
import { parseModerationScores } from "@/src/pages/Posts/moderationScores";
import type { ModerationPost } from "@/src/pages/Posts/posts.type";
import { ROUTES } from "@/src/routes/paths";

type FeedPanelProps = {
  posts: ModerationPost[];
  isLoading: boolean;
  isError: boolean;
};

/**
 * Les publications qui attendent une décision humaine.
 *
 * ## Pourquoi ce panneau existe à côté de celui des signalements
 *
 * Les deux files ne se remplissent pas de la même façon, et les fondre les
 * confondrait : un signalement vient d'un **utilisateur** et court après
 * l'échéance de 24 h ; une publication en attente vient du **classifieur**, qui
 * ne refuse jamais et met de côté ce dont il n'est pas sûr. Sans relève, elle
 * reste invisible pour toujours — c'est une urgence d'une autre nature, pas
 * d'un autre degré.
 *
 * ⚠️ **La vignette n'est pas un ornement.** Sur cet écran on ne tranche pas,
 * on décide s'il faut aller trancher : voir qu'il s'agit de figurines peintes
 * plutôt que d'une photo douteuse est exactement ce qui répond à la question.
 *
 * ⚠️ **L'âge est un temps relatif ordinaire, sans jauge.** L'échéance de 24 h du
 * poste de garde vient de la directive App Store 1.2, qui parle de contenu
 * *signalé* : l'appliquer ici inventerait une règle qui n'existe pas.
 */
export function FeedPanel({ posts, isLoading, isError }: FeedPanelProps) {
  const t = useTranslations("dashboard");
  const tp = useTranslations("posts");
  const { locale } = useLocale();

  const now = Date.now();

  /*
    Les lignes sont préparées **avant** le JSX, et pas seulement pour la
    lisibilité : un littéral d'objet posé directement en attribut est recréé à
    chaque rendu et casse la mémoïsation du composant qui le reçoit — c'est ce
    que signale `react-perf`, et la même raison qui sort les props constantes
    des graphiques dans `chartTheme.ts`.

    ⚠️ L'auteur du fil n'a pas la forme des autres identités du contrat
    (`accountId` / `avatar`) : `AccountBadge` attend un `AccountFace`, où `id`
    est **toujours** l'identifiant de compte — c'est lui qui ouvre la fiche.
  */
  const lignes = posts.map((post) => ({
    id: post.id,
    photo: post.photos[0],
    age: relativeTime(post.createdAt, locale, now),
    createdAt: post.createdAt,
    label: post.type === "gallery" ? tp("type-gallery") : tp("type-thread"),
    scores: parseModerationScores(post.moderationScores),
    face: {
      id: post.author.accountId,
      username: post.author.username,
      avatarUrl: post.author.avatar,
    },
  }));

  return (
    <Panel
      emptyLabel={t("feed-empty")}
      errorLabel={t("panel-error")}
      // Le panneau liste la **file** (`status=pending_review`) : « tout voir »
      // doit donc mener à la file, et non au fil entier.
      href={ROUTES.postsQueue}
      isEmpty={posts.length === 0}
      isError={isError}
      isLoading={isLoading}
      rows={FEED_PREVIEW}
      seeAll={t("see-all")}
      seeAllLabel={t("see-all-feed")}
      title={t("feed-title")}
    >
      {lignes.map((ligne) => (
        <li className="flex items-center gap-3 py-2.5" key={ligne.id}>
          {/* `alt=""` : le type de publication est écrit juste à côté, et le
              contenu de l'image ne se décrit pas depuis ici. */}
          {ligne.photo ? (
            <img
              alt=""
              className="size-9 shrink-0 rounded-md object-cover"
              loading="lazy"
              src={ligne.photo.url}
            />
          ) : (
            <div
              aria-hidden
              className="size-9 shrink-0 rounded-md bg-default"
            />
          )}

          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-medium">{ligne.label}</span>

            <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted">
              <AccountBadge account={ligne.face} isLinked />
            </div>
          </div>

          {/* Le verdict de la machine : c'est lui qui dit si la ligne demande
              un regard attentif ou une validation de routine. */}
          <div className="ms-auto flex shrink-0 items-center gap-2">
            <ScoreBadge scores={ligne.scores} />

            {ligne.age ? (
              <span className="text-xs text-muted" title={ligne.createdAt}>
                {ligne.age}
              </span>
            ) : null}
          </div>
        </li>
      ))}
    </Panel>
  );
}
